import {groupBy} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import {Draft, produce} from 'immer';
import React, {useEffect, useState} from 'react';

import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import AssaySchemaErrorList from 'results/assay-schemas/AssaySchemaErrorList';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';

import {automationFileConfigStyles, ConfigEditorProps} from './AutomationFileConfigUtils';
import {AddComponentButton} from './RunSchemaEditorActionButton';
import {
  AutomationInputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  FileConfigErrors,
} from './RunSchemaSpecConstants';

export interface BaseAutomationFileConfigListEditorProps {
  baseSchema: AssayRunSchema;
  errors: FileConfigErrors;
  onChange: Function;
  onValidationChange: (isValid: boolean) => void;
}

export interface AutomationFileConfigListEditorProps<
  T extends AutomationInputFileConfigSpec | ClientAutomationOutputFileConfigSpec,
> extends BaseAutomationFileConfigListEditorProps {
  automationFileConfigs: Array<T>;
  createNewConfig: () => Draft<T>;
  configEditorClass: React.ComponentType<ConfigEditorProps<T>>;
  getConfigEditorProps: (config: T, existingConfigs: Array<T>) => {};
  title: string;
  placeholder: string;
  addButtonLabel: string;
  dataTestAttrs: DataTestAttrs;
}

export function AutomationFileConfigListEditor<
  T extends AutomationInputFileConfigSpec | ClientAutomationOutputFileConfigSpec,
>(props: AutomationFileConfigListEditorProps<T>): JSX.Element {
  const [invalidConfigs, setInvalidConfigs] = useState(new Set());
  /*
   * When a user clicks 'Update' in the Run Schema UI, the parent component (AssaySchemaEditor) is completely
   * re-rendered, so existingConfigs is also updated. If this were not the case, we would need
   * to explicitly listen for a change in the parent component to know when to update this state variable.
   */
  const [existingConfigs, setExistingConfigs] = useState(props.automationFileConfigs);

  const hasDuplicateNames = (configs: Array<T>): boolean => {
    const allNames = configs.map((configs) => configs.name);
    return new Set(allNames).size !== allNames.length;
  };

  const isDuplicatedName = (configs: Array<T>, currentConfig: T): boolean => {
    const groupedByName = groupBy(configs, (config) => config.name);
    return groupedByName[currentConfig.name].length > 1;
  };

  useEffect(() => {
    props.onValidationChange(invalidConfigs.size === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidConfigs]);

  return (
    <div {...getTestProps(props.dataTestAttrs)}>
      <div
        className={css(styles.editorTitle)}
        {...getTestProps({component: 'AutomationFileConfigListEditor', element: 'title'})}
      >
        <span className={css(styles.editorTitleText)}>{props.title}</span>
        <AddComponentButton
          label={props.addButtonLabel}
          onClick={() => {
            const newConfigs = produce(props.automationFileConfigs, (draftAutomationFileConfigs) => {
              draftAutomationFileConfigs.push(props.createNewConfig());
              return draftAutomationFileConfigs;
            });
            props.onChange(newConfigs);
          }}
          dataTestAttrs={{component: 'AutomationFileConfigListEditor', element: 'add'}}
        />
      </div>
      <AssaySchemaErrorList errors={props.errors.generic} component={props.title} />
      {props.automationFileConfigs.length > 0 ? (
        props.automationFileConfigs.map((config, idx) => (
          <props.configEditorClass
            {...props.getConfigEditorProps(config, existingConfigs)}
            baseSchema={props.baseSchema}
            onValidationChange={(isValid) => {
              const newInvalidConfigs = produce(invalidConfigs, (draftInvalidConfigs) => {
                if (isValid) {
                  draftInvalidConfigs.delete(idx);
                } else {
                  draftInvalidConfigs.add(idx);
                }
                return draftInvalidConfigs;
              });
              setInvalidConfigs(newInvalidConfigs);
            }}
            onUpdateConfig={(newConfig) => {
              const newConfigs = [...props.automationFileConfigs];
              newConfigs.splice(idx, 1, newConfig);
              props.onValidationChange(!hasDuplicateNames(newConfigs));
              props.onChange(newConfigs);
            }}
            onRemoveConfig={() => {
              const newConfigs = produce(props.automationFileConfigs, (draftAutomationFileConfigs) => {
                draftAutomationFileConfigs.splice(idx, 1);
                return draftAutomationFileConfigs;
              });
              const newInvalidConfigs = produce(invalidConfigs, (draftInvalidConfigs) => {
                draftInvalidConfigs.delete(idx);
                return draftInvalidConfigs;
              });
              props.onChange(newConfigs);
              setInvalidConfigs(newInvalidConfigs);
              if (idx <= existingConfigs.length - 1) {
                const newExistingConfigs = produce(existingConfigs, (draftExistingConfigs) => {
                  existingConfigs.splice(idx, 1);
                  return draftExistingConfigs;
                });
                setExistingConfigs(newExistingConfigs);
              }
            }}
            errors={props.errors[idx] ? props.errors[idx] : []}
            key={idx}
            index={idx}
            isDuplicateName={isDuplicatedName(props.automationFileConfigs, config)}
          />
        ))
      ) : (
        <div
          className={css(automationFileConfigStyles.description, styles.placeholder)}
          {...getTestProps({component: 'AutomationFileConfigListEditor', element: 'placeholder'})}
        >
          {props.placeholder}
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  editorTitle: {
    color: Colors.NEAR_BLACK,
    fontSize: '13px',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
  },
  editorTitleText: {
    paddingBottom: '8px',
    paddingTop: '8px',
  },
  placeholder: {
    marginBottom: '32px',
  },
});
